import {
    LineGrade,
    MIN_BASELINE_RUNS,
    RateAggregate,
} from '../../../smart/production-performance';

/**
 * The weekly audit's z-score layer, sitting ON TOP of gradeLine.
 *
 * gradeLine is the app's canonical, one-directional throughput grade: it flags
 * under-performance (ratio < a threshold) against a totals-over-totals baseline.
 * It cannot see the UPSIDE — a suspiciously high number, usually a capture error
 * — because a fast run is simply "good" to a ratio. The audit keeps a z-score for
 * exactly that: |z| flags anomalies in BOTH directions.
 *
 * A z needs the mean and sample stddev of the per-run RATES, which cannot be
 * reconstructed from the sums gradeLine uses. Those live on the SAME rate
 * aggregate (getMachineProductRates, extended), so this reads them off it — one
 * primitive, one cache, both models.
 *
 * It deliberately mirrors gradeLine's two decisions instead of re-deriving them:
 *   - UNIT: whatever gradeLine graded (bultos-first, kg/hr fallback for weighed
 *     lines). So a weighed line still gets a z, on kilos.
 *   - SOURCE: the combo baseline, or the machine fallback gradeLine dropped to
 *     when the combo's history was too thin.
 * That keeps the two signals talking about the same population and unit.
 *
 * Include-self (the run stays in its own mean/std), matching the monthly audit
 * this replaces. At n ≥ MIN_BASELINE_RUNS (10) the self-inclusion bias is
 * negligible, and 10 is where a stddev is stable enough for a z to mean anything
 * — the same floor gradeLine uses for its ratio.
 */

export type ZLevel = 'none' | 'warn' | 'alert';

// |z| thresholds. Magnitude only, both directions: a run far BELOW the mean is
// as much an anomaly as one far above.
export const Z_WARN = 1; // |z| > 1 → atención (amber)
export const Z_ALERT = 2; // |z| > 2 → alerta (red)

export interface WeeklyZFlag {
    // Signed z-score, or null when the baseline is too thin to grade.
    z: number | null;
    level: ZLevel;
    // The unit the z was taken in — mirrors the ratio grade's unit, for the UI.
    unit: 'bultos' | 'kilos' | null;
    // The distribution the z was measured against, surfaced so the UI can explain
    // the flag in a tooltip (μ, σ, and the run count). mean/std are null when the
    // baseline was too thin; n is the count that entered the distribution, which
    // the tooltip uses to say "N corridas, se requieren 10".
    mean: number | null;
    std: number | null;
    n: number;
}

const NO_Z: WeeklyZFlag = {
    z: null,
    level: 'none',
    unit: null,
    mean: null,
    std: null,
    n: 0,
};

function zLevel(z: number | null): ZLevel {
    if (z === null) return 'none';
    const mag = Math.abs(z);
    if (mag > Z_ALERT) return 'alert';
    if (mag > Z_WARN) return 'warn';
    return 'none';
}

export function weeklyZFlag({
    grade,
    groups,
    kilos,
    hours,
    comboRate,
    machineRate,
}: {
    grade: LineGrade;
    groups: number;
    kilos: number;
    hours: number | null;
    comboRate: RateAggregate | undefined;
    machineRate: RateAggregate | undefined;
}): WeeklyZFlag {
    // Only when gradeLine actually settled on a unit. If it refused the run
    // (no hours, thin history, pre-epoch) there is nothing to take a z against.
    if (grade.throughput.ungradable !== null || !hours || hours <= 0) {
        return NO_Z;
    }
    const unit = grade.throughput.unit;
    // gradeLine only ever grades bultos against the COMBO (never the machine
    // mix, whose packaging conventions differ per product), so a bultos unit
    // always implies the combo aggregate; a kilos unit follows the source.
    const agg = grade.baselineSource === 'machine' ? machineRate : comboRate;
    if (!agg) return { ...NO_Z, unit };

    const mean =
        unit === 'bultos' ? agg.all_groups_rate_mean : agg.all_kilos_rate_mean;
    const std =
        unit === 'bultos' ? agg.all_groups_rate_std : agg.all_kilos_rate_std;
    const n = unit === 'bultos' ? agg.all_groups_rate_n : agg.all_kilos_rate_n;

    // mean/std ride the aggregate as Maybe<number> (null when the distribution
    // was too thin server-side, undefined when the field wasn't resolved) — treat
    // both as "no baseline" so the arithmetic below sees plain numbers. `n` is
    // still returned so the tooltip can say how thin the history is.
    if (
        mean === null ||
        mean === undefined ||
        std === null ||
        std === undefined ||
        std <= 0 ||
        n < MIN_BASELINE_RUNS
    ) {
        return { ...NO_Z, unit, mean: mean ?? null, std: std ?? null, n };
    }

    const rate = unit === 'bultos' ? groups / hours : kilos / hours;
    const z = (rate - mean) / std;
    return { z, level: zLevel(z), unit, mean, std, n };
}
